import { useCallback } from 'react';
import { useSetAtom } from 'jotai';
import { useQueryClient } from '@tanstack/react-query';
import { resetConversationStateAtom } from '../atoms/conversationAtoms';
import { endStreamingAtom } from '../atoms/streamingAtoms';
import { clearSearchInputAtom, pendingConversationAtom } from '../atoms/uiAtoms';

/**
 * Centralized state reset for all client state
 *
 * Call this when:
 * - User logs out
 * - User switches accounts
 * - Need to clear all cached data
 *
 * Handles:
 * - TanStack Query cache (server data)
 * - Jotai atoms (client state)
 *
 * When adding new atoms, add their reset here too.
 * Note: soundEnabledAtom is intentionally excluded — it is a device-level
 * preference persisted to localStorage that should survive logout.
 */
export function useResetState() {
	const queryClient = useQueryClient();
	const endStreaming = useSetAtom(endStreamingAtom);
	const clearSearchInput = useSetAtom(clearSearchInputAtom);
	const resetConversation = useSetAtom(resetConversationStateAtom);
	const setPendingConversation = useSetAtom(pendingConversationAtom);

	return useCallback(() => {
		// 1. Clear all TanStack Query cache
		queryClient.clear();

		// 2. Reset Jotai atoms
		endStreaming();
		clearSearchInput();
		resetConversation();
		setPendingConversation(null);

		// Add new state resets here as you add new atoms
	}, [queryClient, endStreaming, clearSearchInput, resetConversation, setPendingConversation]);
}
